import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Home, Search, MessageCircle, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";

export default function NotFoundPage() {
  const [trendingQuestions, setTrendingQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch trending/popular questions
    const fetchTrending = async () => {
      try {
        const res = await api.get("/feed?sort=top&limit=5");
        setTrendingQuestions(res.data || []);
      } catch (e) {
        console.error("Failed to fetch trending:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchTrending();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex flex-col items-center justify-center px-4 py-12">
      <Seo 
        title="Page Not Found" 
        description="Oops! This page went incognito. Browse trending questions or ask something new on AskIncog."
        url="/404"
      />
      
      {/* 404 Hero */}
      <div className="text-center mb-8">
        <h1 className="text-8xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-500 mb-4">
          404
        </h1>
        <p className="text-2xl text-slate-300 mb-2">
          Oops, this question went incognito! 🤐
        </p>
        <p className="text-slate-500 max-w-md mx-auto">
          The page you&apos;re looking for doesn&apos;t exist or has been moved. 
          But don&apos;t worry — there&apos;s plenty more to explore!
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-4 justify-center mb-12">
        <Link to="/">
          <Button className="bg-indigo-600 hover:bg-indigo-500 text-white">
            <Home className="mr-2 h-4 w-4" />
            Go Home
          </Button>
        </Link>
        <Link to="/browse">
          <Button variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800">
            <Search className="mr-2 h-4 w-4" />
            Browse Questions
          </Button>
        </Link>
        <Link to="/#ask">
          <Button variant="outline" className="border-indigo-500 text-indigo-400 hover:bg-indigo-500/10">
            <MessageCircle className="mr-2 h-4 w-4" />
            Ask a Question
          </Button>
        </Link>
      </div>

      {/* Trending Questions */}
      <div className="w-full max-w-2xl">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="h-5 w-5 text-amber-400" />
          <h2 className="text-lg font-semibold text-slate-200">Trending Right Now</h2>
        </div>
        
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 bg-slate-800/50 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : trendingQuestions.length > 0 ? (
          <div className="space-y-3">
            {trendingQuestions.map((q, index) => (
              <Link key={q.slug || index} to={`/q/${q.slug}`}>
                <Card className="p-4 bg-slate-800/50 border-slate-700 hover:border-indigo-500/50 hover:bg-slate-800 transition-all cursor-pointer">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl font-bold text-slate-600">
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-slate-200 font-medium line-clamp-2">
                        {q.title || q.body?.slice(0, 80)}
                      </h3>
                      <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                        <span>{q.answer_count || 0} answers</span>
                        <span>{q.view_count || 0} views</span>
                        {q.intent && (
                          <span className="px-2 py-0.5 bg-slate-700 rounded-full">
                            {q.intent}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <Card className="p-6 bg-slate-800/50 border-slate-700 text-center">
            <p className="text-slate-400">No trending questions yet. Be the first to ask!</p>
          </Card>
        )}
      </div>

      {/* Footer Link */}
      <p className="mt-12 text-sm text-slate-600">
        Think this is a mistake?{" "}
        <a href="mailto:hello@askincog.com" className="text-indigo-400 hover:underline">
          Let us know
        </a>
      </p>
    </div>
  );
}
